import type {
  DashboardResponse,
  HouseholdProfilesResponse,
  RbimDataFile,
  RbimDataImportPreviewResponse,
  RbimDataImportResponse,
} from "@rbim/shared";
import { Database, Download, RefreshCw, ShieldCheck, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { ApiRequestError, apiRequest } from "../api";
import { useAuth } from "../auth";
import { parseOnlineRbimImportFile } from "../legacy-rbim-import";
import { createRbimWorkbookBlob } from "../data-management-xlsx";

export function DataManagementPage() {
  const { user } = useAuth();
  const [households, setHouseholds] = useState<HouseholdProfilesResponse | null>(null);
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [error, setError] = useState("");
  const [transferMessage, setTransferMessage] = useState("");
  const [transferBusy, setTransferBusy] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importPayload, setImportPayload] = useState<RbimDataFile | null>(null);
  const [importPreview, setImportPreview] = useState<RbimDataImportPreviewResponse | null>(null);
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);

  const canDeleteRecords = user?.role === "MUNICIPAL_CITY_ADMINISTRATOR"
    && user.permissions.includes("record.delete");
  const transferScope = user?.scope.type === "BARANGAY"
    ? "barangay"
    : user?.scope.type === "MUNICIPALITY_CITY"
      ? "municipality"
      : null;
  const canImportData = Boolean(transferScope && user?.permissions.includes("data.import"));
  const canExportData = Boolean(transferScope && user?.permissions.includes("data.export"));
  const scopeLabel = user?.scope.type === "BARANGAY" ? "Barangay" : "Municipality/City";

  const loadOverview = useCallback(async () => {
    try {
      const [profileResponse, dashboardResponse] = await Promise.all([
        apiRequest<HouseholdProfilesResponse>("/profiles/households?page=1&pageSize=10"),
        apiRequest<DashboardResponse>("/dashboard"),
      ]);
      setHouseholds(profileResponse);
      setDashboard(dashboardResponse);
      setError("");
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Data management information could not be loaded.",
      );
    }
  }, []);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  async function handleImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0] ?? null;
    setTransferMessage("");
    setImportPreview(null);
    setImportPayload(null);
    setImportWarnings([]);
    setImportFileName(file?.name ?? "");
    setImportConfirmOpen(false);
    if (!file || !transferScope) return;

    setTransferBusy(true);
    try {
      const parsed = await parseOnlineRbimImportFile(file);
      const preview = await apiRequest<RbimDataImportPreviewResponse>(
        `/${transferScope}/data-management/import/preview`,
        { method: "POST", body: JSON.stringify(parsed.dataFile) },
      );
      setImportPayload(parsed.dataFile);
      setImportPreview(preview);
      setImportWarnings(parsed.warnings);
      setTransferMessage(
        preview.importableRecords > 0
          ? `Preview complete. ${preview.importableRecords.toLocaleString()} record${preview.importableRecords === 1 ? " is" : "s are"} ready to import${parsed.skippedRows ? `; ${parsed.skippedRows.toLocaleString()} incomplete legacy row(s) were skipped` : ""}.`
          : "Preview complete. All valid records in this file already exist in the authorized scope.",
      );
    } catch (requestError) {
      setTransferMessage(
        requestError instanceof ApiRequestError
          ? requestError.message
          : requestError instanceof SyntaxError
            ? "The selected RBIM file contains invalid JSON."
            : requestError instanceof Error
              ? requestError.message
              : "The RBIM import file could not be previewed.",
      );
    } finally {
      setTransferBusy(false);
    }
  }

  async function importRecords() {
    if (!transferScope || !importPayload || !importPreview || importPreview.importableRecords === 0) return;

    setTransferBusy(true);
    setTransferMessage("");
    try {
      const result = await apiRequest<RbimDataImportResponse>(
        `/${transferScope}/data-management/import`,
        { method: "POST", body: JSON.stringify(importPayload) },
      );
      setTransferMessage(
        `Import completed: ${result.importedRecords.toLocaleString()} imported, ${result.duplicateRecords.toLocaleString()} duplicate${result.duplicateRecords === 1 ? "" : "s"} skipped.`,
      );
      setImportConfirmOpen(false);
      setImportPreview({
        totalRecords: result.totalRecords,
        importableRecords: 0,
        duplicateRecords: result.totalRecords,
      });
      await loadOverview();
    } catch (requestError) {
      setTransferMessage(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "The RBIM import could not be completed.",
      );
    } finally {
      setTransferBusy(false);
    }
  }

  async function exportRecords() {
    if (!transferScope) return;
    setTransferBusy(true);
    setTransferMessage("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 120_000);
    try {
      setTransferMessage("Requesting authorized records for Excel export…");
      const data = await apiRequest<RbimDataFile>(
        `/${transferScope}/data-management/export`,
        { signal: controller.signal },
      );
      setTransferMessage("Preparing the Excel workbook for download…");
      // Allow React to display progress before potentially expensive workbook generation.
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0));
      const blob = createRbimWorkbookBlob(data);
      if (blob.size === 0) {
        throw new Error("The RBIM Excel workbook is empty and could not be downloaded.");
      }
      const downloadUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = `RBIM_Records_${formatExportTimestamp(new Date())}.xlsx`;
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        anchor.remove();
        // Keep the object URL alive while the browser starts the download.
        // Revoking it synchronously can cancel downloads in some browsers.
        window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 60_000);
      }
      setTransferMessage(
        `Export prepared with ${data.records.length.toLocaleString()} record${data.records.length === 1 ? "" : "s"} from your authorized ${scopeLabel} scope.`,
      );
    } catch (requestError) {
      setTransferMessage(
        requestError instanceof ApiRequestError
          ? requestError.message
          : requestError instanceof Error && requestError.name === "AbortError"
            ? "Excel export timed out after 2 minutes while requesting records. Please try again or contact the system administrator."
            : requestError instanceof Error
              ? requestError.message
              : "The RBIM export could not be prepared.",
      );
    } finally {
      window.clearTimeout(timeout);
      setTransferBusy(false);
    }
  }

  return <section className="portal-page">
    <header className="page-heading">
      <div>
        <span className="eyebrow">Authorized data operations</span>
        <h1>Data Management</h1>
        <p>Import extracted or legacy RBIM records, or export canonical online records within your authorized geographic scope.</p>
      </div>
      <Database className="page-heading-icon" />
    </header>
    {error ? <div className="form-error" role="alert">{error}</div> : null}
    {transferMessage ? <div className="notice-card" role="status"><strong>{transferMessage}</strong></div> : null}
    <section className="module-grid data-management-grid">
      <article className="module-card">
        <span className="module-icon"><Database /></span>
        <div><span className="eyebrow">Record inventory</span><h2>{households?.totalItems.toLocaleString() ?? "—"} households</h2><p>{dashboard?.summary.totalPopulation.toLocaleString() ?? "—"} residents are represented by authorized submitted PostgreSQL records.</p></div>
      </article>
      <article className="module-card">
        <span className="module-icon"><RefreshCw /></span>
        <div><span className="eyebrow">Recent synchronization</span><h2>{dashboard?.recentActivity.length.toLocaleString() ?? "—"} recent operations</h2><p>Desktop synchronization remains device-bound, idempotent, and Barangay-scoped.</p></div>
      </article>
      <article className="module-card">
        <span className="module-icon"><Download /></span>
        <div><span className="eyebrow">Controlled transfers</span><h2>{scopeLabel} scope</h2><p>Every online import and export is checked again by the server against the signed-in account's permissions and geography.</p></div>
      </article>
    </section>

    {(canImportData || canExportData) ? <section className="maintenance-grid data-management-transfer-grid">
      {canImportData ? <article className="form-card">
        <div className="section-heading">
          <h2><Upload />Import RBIM data</h2>
          <p>Import extracted/canonical RBIM Excel workbooks, current <strong>.rbim/.bak</strong> files, or migrate old RBIM <strong>.xlsx, .xls, and .csv</strong> data. RBIM reconstructs household and resident rows, then the server checks Municipality/Barangay scope and duplicates before saving anything.</p>
        </div>
        <div className="rbim-import-file-field">
          <label className="rbim-import-file-label" htmlFor="rbim-import-file">RBIM data file</label>
          <label className={`rbim-import-file-picker${transferBusy ? " is-disabled" : ""}`} htmlFor="rbim-import-file">
            <span className="rbim-import-file-button"><Upload />Choose file</span>
            <span className={`rbim-import-file-name${importFileName ? " has-file" : ""}`}>
              {importFileName || "No file chosen"}
            </span>
          </label>
          <input
            id="rbim-import-file"
            className="rbim-import-file-input"
            type="file"
            accept=".rbim,.bak,.xlsx,.xls,.csv,application/json,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            disabled={transferBusy}
            onChange={(event) => void handleImportFile(event)}
          />
        </div>
        {importFileName ? <p className="rbim-import-selected"><strong>Selected file:</strong> <span>{importFileName}</span></p> : null}
        {importWarnings.length ? <div className="notice-card">
          <strong>Legacy import notes</strong>
          {importWarnings.map((warning) => <p key={warning}>{warning}</p>)}
        </div> : null}
        {importPreview ? <div className="notice-card">
          <strong>Import preview</strong>
          <p>{importPreview.totalRecords.toLocaleString()} household record{importPreview.totalRecords === 1 ? "" : "s"} · {importPreview.importableRecords.toLocaleString()} ready to import · {importPreview.duplicateRecords.toLocaleString()} duplicate{importPreview.duplicateRecords === 1 ? "" : "s"}</p>
        </div> : null}
        <button
          type="button"
          className="primary-button"
          disabled={transferBusy || !importPayload || !importPreview || importPreview.importableRecords === 0}
          onClick={() => setImportConfirmOpen(true)}
        >
          <Upload />{transferBusy ? "Working…" : "Import validated records"}
        </button>

      </article> : null}

      {canExportData ? <article className="form-card">
        <div className="section-heading">
          <h2><Download />Export RBIM data</h2>
          <p>Download all canonical records inside your authorized {scopeLabel} as an Excel <strong>.xlsx</strong> worksheet for reporting, review, or safekeeping.</p>
        </div>
        <button
          type="button"
          className="primary-button"
          disabled={transferBusy}
          onClick={() => void exportRecords()}
        >
          <Download />{transferBusy ? "Working…" : "Export authorized records"}
        </button>
      </article> : null}
    </section> : null}

    {importConfirmOpen && importPreview ? (
      <section className="rbim-import-confirmation rbim-import-confirmation-wide" role="dialog" aria-labelledby="rbim-import-confirmation-title">
        <div className="rbim-import-confirmation-heading">
          <span className="rbim-import-confirmation-icon"><Upload /></span>
          <div>
            <span className="eyebrow">Import confirmation</span>
            <h3 id="rbim-import-confirmation-title">Import validated RBIM records?</h3>
            <p>Review the import summary before saving records to the Municipality/City database.</p>
          </div>
        </div>
        <div className="rbim-import-confirmation-grid">
          <div><span>Destination</span><strong>{user?.locationLabel ?? scopeLabel}</strong></div>
          <div><span>Ready to import</span><strong>{importPreview.importableRecords.toLocaleString()}</strong></div>
          <div><span>Duplicates</span><strong>{importPreview.duplicateRecords.toLocaleString()}</strong></div>
          <div><span>Selected file</span><strong>{importFileName || "RBIM import file"}</strong></div>
        </div>
        <div className="rbim-import-confirmation-note">
          Duplicate records will be skipped. Only records inside your authorized Municipality/City and Barangay scope will be accepted.
        </div>
        <div className="rbim-import-confirmation-actions">
          <button type="button" className="secondary-button" disabled={transferBusy} onClick={() => setImportConfirmOpen(false)}>Cancel</button>
          <button type="button" className="primary-button" disabled={transferBusy} onClick={() => void importRecords()}>
            <Upload />{transferBusy ? "Importing…" : "Confirm import"}
          </button>
        </div>
      </section>
    ) : null}

    {canDeleteRecords ? <section className="notice-card">
      <strong><Trash2 />Governed record deletion</strong>
      <p>Delete an individual RBIM household record within your Municipality/City when it is a duplicate, dummy/test record, or confirmed encoding error. A meaningful reason is required, and the deletion is retained in the audit trail and synchronization history.</p>
      <Link className="danger-button" to="/households">Open record inventory to delete</Link>
    </section> : null}
    <section className="notice-card">
      <strong><ShieldCheck />Protected data boundary</strong>
      <p>Barangay Supervisor imports are locked to the assigned Barangay. Municipality imports may contain multiple Barangays, but every record must belong to the signed-in Municipality/City. A mixed or out-of-scope file is rejected before any record is written.</p>
    </section>
  </section>;
}

function safeFileName(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "rbim";
}


function formatExportTimestamp(value: Date) {
  const date = value.toISOString().slice(0, 10);
  const hours = value.getHours();
  const minutes = String(value.getMinutes()).padStart(2, "0");
  const hour12 = hours % 12 || 12;
  const meridiem = hours >= 12 ? "PM" : "AM";
  return `${date}_${String(hour12).padStart(2, "0")}-${minutes}-${meridiem}`;
}
